import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Search, ThumbsUp, ThumbsDown, Home } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFAQCategories, useFAQs, useTrackHelpful } from "@/hooks/useFAQs";
import { generateFAQPageSchema } from "@/lib/faqSchemaGenerator";
import { SchemaMarkup } from "@/components/blog/SchemaMarkup";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [votedFAQs, setVotedFAQs] = useState<Set<string>>(new Set());

  const { data: categories, isLoading: categoriesLoading } = useFAQCategories();
  const { data: faqs, isLoading: faqsLoading } = useFAQs(
    selectedCategory === "all" ? undefined : selectedCategory,
    searchTerm
  );
  const trackHelpful = useTrackHelpful();

  const handleVote = (faqId: string, isHelpful: boolean) => {
    if (votedFAQs.has(faqId)) {
      toast.info("You've already voted on this FAQ");
      return;
    }

    trackHelpful.mutate({ faqId, isHelpful });
    setVotedFAQs((prev) => new Set([...prev, faqId]));
    toast.success("Thank you for your feedback!");
  };

  const extractYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const groupedFAQs =
    faqs?.reduce((acc, faq) => {
      const categoryName = faq.faq_categories?.name || "Uncategorized";
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(faq);
      return acc;
    }, {} as Record<string, typeof faqs>) || {};

  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Frequently Asked Questions")}</title>
        <meta name="description" content="Find answers to common questions about iQ Texting's AI-powered SMS platform. Learn about pricing, features, integrations, and technical support." />
        <link rel="canonical" href={generateCanonicalUrl("/faq")} />
        <meta property="og:title" content={generatePageTitle("Frequently Asked Questions")} />
        <meta property="og:description" content="Find answers to common questions about iQ Texting's platform, pricing, and features." />
        <meta property="og:url" content={generateCanonicalUrl("/faq")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
        {faqs && faqs.length > 0 && (
          <SchemaMarkup schema={generateFAQPageSchema(faqs)} />
        )}

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <Home className="h-4 w-4" />
            Back to Home
          </Link>
          
          <Breadcrumbs items={[{ name: "FAQ", url: "/faq" }]} />
          
          <div className="text-center mt-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Find answers to common questions about iQ Texting
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto text-center">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-14 text-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-20 px-4">
        <div className="container max-w-4xl mx-auto">
          {categoriesLoading || faqsLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading FAQs...</p>
            </div>
          ) : (
            <>
              {/* Category Tabs */}
              <Tabs
                value={selectedCategory}
                onValueChange={setSelectedCategory}
                className="mb-8"
              >
                <TabsList className="w-full flex-wrap h-auto justify-start gap-2 bg-transparent">
                  <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    All Questions
                  </TabsTrigger>
                  {categories?.map((category) => (
                    <TabsTrigger
                      key={category.id}
                      value={category.slug}
                      className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      {category.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* All Questions */}
                <TabsContent value="all" className="mt-6">
                  {Object.entries(groupedFAQs).map(([categoryName, categoryFAQs]) => (
                    <div key={categoryName} className="mb-8">
                      <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                        {categoryName}
                        <span className="text-sm text-muted-foreground font-normal">
                          ({categoryFAQs.length} {categoryFAQs.length === 1 ? 'question' : 'questions'})
                        </span>
                      </h2>
                      <Accordion type="single" collapsible className="space-y-4">
                        {categoryFAQs.map((faq, index) => {
                          const youtubeId = faq.video_url ? extractYouTubeId(faq.video_url) : null;
                          
                          return (
                            <AccordionItem 
                              key={faq.id} 
                              id={`faq-${faq.id}`}
                              value={`${categoryName}-${index}`}
                              className="bg-background rounded-lg border px-6"
                            >
                              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                                {faq.question}
                              </AccordionTrigger>
                              <AccordionContent className="pt-2">
                                <div className="prose prose-sm max-w-none mb-4 text-muted-foreground whitespace-pre-wrap">
                                  {faq.answer}
                                </div>
                                
                                {/* YouTube Video Embed */}
                                {youtubeId && (
                                  <div className="mb-4 rounded-lg overflow-hidden">
                                    <iframe
                                      width="100%"
                                      height="400"
                                      src={`https://www.youtube.com/embed/${youtubeId}`}
                                      title={faq.question}
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      className="w-full aspect-video"
                                    />
                                  </div>
                                )}
                                
                                {/* Helpful Voting */}
                                <div className="flex items-center gap-4 pt-4 border-t">
                                  <span className="text-sm text-muted-foreground">
                                    Was this helpful?
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleVote(faq.id, true)}
                                    disabled={votedFAQs.has(faq.id)}
                                    className="gap-2"
                                  >
                                    <ThumbsUp className="h-4 w-4" />
                                    Yes {faq.helpful_count > 0 && `(${faq.helpful_count})`}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleVote(faq.id, false)}
                                    disabled={votedFAQs.has(faq.id)}
                                    className="gap-2"
                                  >
                                    <ThumbsDown className="h-4 w-4" />
                                    No {faq.not_helpful_count > 0 && `(${faq.not_helpful_count})`}
                                  </Button>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  ))}
                </TabsContent>

                {/* Individual Category Tabs */}
                {categories?.map((category) => (
                  <TabsContent key={category.id} value={category.slug} className="mt-6">
                    {faqs && faqs.length > 0 ? (
                      <Accordion type="single" collapsible className="space-y-4">
                        {faqs.map((faq, index) => {
                          const youtubeId = faq.video_url ? extractYouTubeId(faq.video_url) : null;
                          
                          return (
                            <AccordionItem 
                              key={faq.id} 
                              id={`faq-${faq.id}`}
                              value={`item-${index}`}
                              className="bg-background rounded-lg border px-6"
                            >
                              <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                                {faq.question}
                              </AccordionTrigger>
                              <AccordionContent className="pt-2">
                                <div className="prose prose-sm max-w-none mb-4 text-muted-foreground whitespace-pre-wrap">
                                  {faq.answer}
                                </div>
                                
                                {/* YouTube Video Embed */}
                                {youtubeId && (
                                  <div className="mb-4 rounded-lg overflow-hidden">
                                    <iframe
                                      width="100%"
                                      height="400"
                                      src={`https://www.youtube.com/embed/${youtubeId}`}
                                      title={faq.question}
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      className="w-full aspect-video"
                                    />
                                  </div>
                                )}
                                
                                {/* Helpful Voting */}
                                <div className="flex items-center gap-4 pt-4 border-t">
                                  <span className="text-sm text-muted-foreground">
                                    Was this helpful?
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleVote(faq.id, true)}
                                    disabled={votedFAQs.has(faq.id)}
                                    className="gap-2"
                                  >
                                    <ThumbsUp className="h-4 w-4" />
                                    Yes {faq.helpful_count > 0 && `(${faq.helpful_count})`}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleVote(faq.id, false)}
                                    disabled={votedFAQs.has(faq.id)}
                                    className="gap-2"
                                  >
                                    <ThumbsDown className="h-4 w-4" />
                                    No {faq.not_helpful_count > 0 && `(${faq.not_helpful_count})`}
                                  </Button>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-muted-foreground">
                          No FAQs found in this category yet.
                        </p>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>

              {/* No Results */}
              {searchTerm && (!faqs || faqs.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">
                    No FAQs found matching "{searchTerm}"
                  </p>
                  <Button variant="outline" onClick={() => setSearchTerm("")}>
                    Clear Search
                  </Button>
                </div>
              )}
            </>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center p-8 bg-card rounded-lg border">
            <h3 className="text-2xl font-semibold mb-4">
              Still have questions?
            </h3>
            <p className="text-muted-foreground mb-6">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <Link to="/contact">
              <Button size="lg">Contact Support</Button>
            </Link>
          </div>
        </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;
